import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin, Shield, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { t } from '../contexts/translations';
import logo from '../assets/logo-white.png';
import PaymentCards from './PaymentCards';

const Footer = ({ language }) => {
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: <Facebook className="w-5 h-5" />, label: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: <Instagram className="w-5 h-5" />, label: 'Instagram', color: 'hover:bg-gradient-to-tr from-purple-500 via-pink-500 to-yellow-500' },
    { icon: <Twitter className="w-5 h-5" />, label: 'Twitter', color: 'hover:bg-sky-500' },
  ];

  const columnVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  return (
    <footer className="relative z-10 bg-gradient-to-br from-[#d97706] via-[#b45309] to-[#92400e] text-white pt-24 pb-12 px-6 rounded-t-[3rem] md:rounded-t-[5rem] overflow-hidden">
      {/* Premium Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-30">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute -bottom-24 -right-24 w-[30rem] h-[30rem] bg-orange-400/20 blur-[150px] rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-12 mb-20">
          {/* Brand Column */}
          <motion.div 
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={columnVariants}
            className="flex flex-col items-center md:items-start text-center md:text-left"
          >
            <motion.div 
              whileHover={{ rotate: [-1, 1, -1, 0], scale: 1.05 }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <img 
                src={logo} 
                alt="Palais Touristique" 
                className="h-28 w-auto brightness-125 drop-shadow-[0_10px_30px_rgba(255,255,255,0.2)]"
              />
            </motion.div>
            <p className="text-orange-50/90 font-medium leading-relaxed mb-10 max-w-sm text-lg italic">
              " {t('trustPartnerDesc', language)} "
            </p>
            <div className="flex space-x-5">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href="#!"
                  whileHover={{ y: -8, scale: 1.15, backgroundColor: 'rgba(255,255,255,0.2)' }}
                  whileTap={{ scale: 0.9 }}
                  className={`w-14 h-14 bg-white/10 backdrop-blur-xl border border-white/20 rounded-[1.25rem] flex items-center justify-center transition-all duration-500 hover:border-white/50 hover:shadow-[0_10px_25px_rgba(0,0,0,0.2)] cursor-pointer`}
                  aria-label={social.label}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Company Column */}
          <motion.div 
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={columnVariants}
            className="flex flex-col items-center md:items-start text-center md:text-left"
          >
            <h3 className="text-sm font-black mb-10 uppercase tracking-[0.3em] text-orange-200">
              {t('company', language)}
            </h3>
            <ul className="space-y-6">
              {[
                { to: "/about", label: 'aboutUs' },
                { to: "/contact", label: 'contactUs' }
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    onClick={handleLinkClick} 
                    className="group flex items-center gap-4 text-white/80 hover:text-white transition-all font-bold text-lg"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 group-hover:scale-[2.5] group-hover:bg-white transition-all duration-300" />
                    {t(link.label, language)}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Support Column */}
          <motion.div 
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={columnVariants}
            className="flex flex-col items-center md:items-start text-center md:text-left"
          >
            <h3 className="text-sm font-black mb-10 uppercase tracking-[0.3em] text-orange-200">
              {t('support', language)}
            </h3>
            <ul className="space-y-6">
              {[
                { to: "/help", label: 'helpCenter' },
                { to: "/cancellation", label: 'cancellation' },
                { to: "/safety", label: 'safety' }
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    onClick={handleLinkClick} 
                    className="group flex items-center gap-4 text-white/80 hover:text-white transition-all font-bold text-lg"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 group-hover:scale-[2.5] group-hover:bg-white transition-all duration-300" />
                    {t(link.label, language)}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal Column */}
          <motion.div 
            custom={3}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={columnVariants}
            className="flex flex-col items-center md:items-start text-center md:text-left"
          >
            <h3 className="text-sm font-black mb-10 uppercase tracking-[0.3em] text-orange-200">
              {t('legal', language)}
            </h3>
            <ul className="space-y-6">
              {[
                { to: "/legal/privacy", label: 'privacy' },
                { to: "/legal/terms", label: 'terms' },
                { to: "/cgv", label: 'cgvTitle' },
                { to: "/legal/mentions", label: 'mentionsTitle' }
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    onClick={handleLinkClick} 
                    className="group flex items-center gap-4 text-white/80 hover:text-white transition-all font-bold text-lg"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 group-hover:scale-[2.5] group-hover:bg-white transition-all duration-300" />
                    {t(link.label, language)}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Ultra-Modern Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <motion.a 
            href="mailto:Contact@palaistouristique.ma"
            whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
            className="bg-white/5 backdrop-blur-3xl rounded-[2.5rem] p-8 border border-white/10 flex items-center gap-6 group cursor-pointer transition-all duration-500"
          >
            <div className="w-16 h-16 shrink-0 bg-white/10 rounded-2xl flex items-center justify-center group-hover:bg-white/20 group-hover:rotate-6 transition-all duration-500">
              <Mail className="w-8 h-8" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-orange-200 mb-1">{t('emailUs', language)}</p>
              <p className="font-extrabold text-base md:text-lg tracking-tight break-all leading-tight">Contact@palaistouristique.ma</p>
            </div>
          </motion.a>
          
          <motion.a 
            href="tel:0666035657"
            whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
            className="bg-white/5 backdrop-blur-3xl rounded-[2.5rem] p-8 border border-white/10 flex items-center gap-6 group cursor-pointer transition-all duration-500"
          >
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center group-hover:bg-white/20 group-hover:-rotate-6 transition-all duration-500">
              <Phone className="w-8 h-8" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-orange-200 mb-1">{t('callUs', language)}</p>
              <p className="font-extrabold text-xl tracking-tight">06 66 03 56 57</p>
            </div>
          </motion.a>

          <motion.div 
            whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
            className="bg-white/5 backdrop-blur-3xl rounded-[2.5rem] p-8 border border-white/10 flex items-center gap-6 group transition-all duration-500"
          >
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 transition-all duration-500">
              <MapPin className="w-8 h-8" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-orange-200 mb-1">{t('footerLocation', language)}</p>
              <p className="font-extrabold text-lg leading-tight tracking-tight">Place Atlas, 7 Rue Hassan Dkhissi, Fès 30000</p>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="pt-12 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-8"
        >
          <div className="flex flex-col items-center md:items-start gap-2">
            <p className="text-orange-100 text-sm font-bold tracking-wide">
              &copy; {currentYear} <span className="text-white font-black uppercase tracking-widest">Palais Touristique</span>
            </p>
            <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.3em]">
              {t('footerRights', language)}
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <PaymentCards language={language} showLabel={false} />
            <motion.div whileHover={{ scale: 1.05 }} className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-black text-white/50 hover:text-white transition-all cursor-default">
              <Shield className="w-5 h-5 text-orange-400" />
              {t('securedByCMI', language)}
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-black text-white/50 hover:text-white transition-all cursor-default">
              <Sparkles className="w-5 h-5 text-orange-400" />
              Made by KYBOHO
            </motion.div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;